import type { FileNode } from './protocol';

/** Walk the tree to the folder whose `path` matches `relPath` ("" = share root). */
export function nodeAtPath(root: FileNode, relPath: string): FileNode {
  if (!relPath) return root;
  let node = root;
  const parts = relPath.split('/').filter(Boolean);
  let acc = '';
  for (const part of parts) {
    acc = acc ? `${acc}/${part}` : part;
    const child = node.children?.find((c) => c.path === acc);
    if (!child) return node;
    node = child;
  }
  return node;
}

/** Breadcrumb trail from share root to `relPath`. */
export function trailAtPath(root: FileNode, relPath: string): FileNode[] {
  if (!relPath) return [root];
  const out: FileNode[] = [root];
  let node = root;
  const parts = relPath.split('/').filter(Boolean);
  let acc = '';
  for (const part of parts) {
    acc = acc ? `${acc}/${part}` : part;
    const child = node.children?.find((c) => c.path === acc);
    if (!child) break;
    out.push(child);
    node = child;
  }
  return out;
}

/** Merge a one-level listing into an existing tree (on-demand subfolder loads). */
export function mergeListResponse(prev: FileNode | null, listed: FileNode): FileNode {
  if (!prev) return listed;
  const rel = listed.path ?? '';
  if (!rel) return listed;
  if (rel === prev.path) return listed;
  return patchChildrenAtPath(prev, rel, listed.children ?? []);
}

function patchChildrenAtPath(root: FileNode, relPath: string, children: FileNode[]): FileNode {
  if (!relPath) {
    return { ...root, children };
  }
  const parts = relPath.split('/').filter(Boolean);
  const mergeKids = (node: FileNode, depth: number): FileNode => {
    if (depth >= parts.length) {
      return { ...node, children };
    }
    const acc = parts.slice(0, depth + 1).join('/');
    const kids = node.children ?? [];
    const idx = kids.findIndex((c) => c.path === acc);
    if (idx < 0) return node;
    const next = mergeKids(kids[idx], depth + 1);
    if (next === kids[idx]) return node;
    const copy = kids.slice();
    copy[idx] = next;
    return { ...node, children: copy };
  };
  return mergeKids(root, 0);
}
